import React, { useState, useEffect, useRef } from 'react';
import { 
  Sparkles, 
  X, 
  ChevronRight, 
  Search, 
  Rocket, 
  ArrowLeft,
  MessageSquare,
  Send,
  Loader2,
  MapPin,
  Compass,
  Phone,
  MessageCircle,
  Check,
  CheckCircle2,
  Plus,
  AlertCircle,
  HelpCircle,
  ExternalLink,
  ShieldAlert
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';

type Step = 
  | 'help'
  | 'lead_capture';

type LeadCaptureSubStep = 
  | 'request_desc'
  | 'budget_input'
  | 'location_select'
  | 'contact_pref'
  | 'phone_input'
  | 'success_matching';

export const SmartAssistant: React.FC<{ inline?: boolean }> = ({ inline = false }) => {
  const { user, profile } = useAuth();
  const [isOpen, setIsOpen] = useState(inline ? true : false);
  const [step, setStep] = useState<Step>('lead_capture');
  const [selectedFaq, setSelectedFaq] = useState<number | null>(null);
  const navigate = useNavigate();

  // LEAD CAPTURE ENGINE STATES
  const [subStep, setSubStep] = useState<LeadCaptureSubStep>('request_desc');
  const [leadRequest, setLeadRequest] = useState('');
  const [budgetMin, setBudgetMin] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [customerLocation, setCustomerLocation] = useState('');
  const [contactPreference, setContactPreference] = useState<'WhatsApp' | 'Call' | 'Both' | null>(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);
  const [createdLead, setCreatedLead] = useState<any>(null);
  const [matches, setMatches] = useState<any[]>([]);

  const matchesRef = useRef<any[]>([]);
  matchesRef.current = matches;

  // Botswana major locations for zero typing friction
  const BOTSWANA_TOWNS = [
    "Gaborone", "Francistown", "Maun", "Palapye", "Kanye", 
    "Mochudi", "Molepolole", "Serowe", "Lobatse", "Selebi-Phikwe"
  ];

  // Process Lead capture step transitions
  const handleNextSubStep = async () => {
    if (subStep === 'request_desc') {
      if (!leadRequest.trim()) {
        toast.error("Please tell us what product or service you need.");
        return;
      }
      setSubStep('budget_input');
    } else if (subStep === 'budget_input') {
      // Budget can be blank/skipped
      setSubStep('location_select');
    } else if (subStep === 'location_select') {
      if (!customerLocation) {
        toast.error("Please pick an operational location.");
        return;
      }
      setSubStep('contact_pref');
    } else if (subStep === 'contact_pref') {
      if (!contactPreference) {
        toast.error("Please explicitly select your contact preference (WhatsApp, Call, or Both).");
        return;
      }
      setSubStep('phone_input');
    } else if (subStep === 'phone_input') {
      if (!phoneNumber.trim()) {
        toast.error("Suppliers need your contact number to connect.");
        return;
      }
      // Submit Lead
      await submitLeadToDatabase();
    }
  };

  const submitLeadToDatabase = async () => {
    if (isSubmittingLead) return;
    try {
      setIsSubmittingLead(true);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);
      const res = await fetch('/api/leads/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          request_text: leadRequest.trim(),
          budget_min: budgetMin || null,
          budget_max: budgetMax || null,
          customer_location: customerLocation,
          contact_preference: contactPreference,
          phone_number: phoneNumber.trim()
        })
      });
      clearTimeout(timeoutId);

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData?.error || "We couldn't submit your request right now. Please try again.");
      }

      const data = await res.json();
      if (!data?.success || !data?.request?.id) {
        throw new Error(data?.error || "Failed to confirm your sourcing request. Please try again.");
      }

      setCreatedLead(data.request);
      setSubStep('success_matching');
      toast.success("Lead broadcasted! We are matching you with local verified suppliers.");
      fetchInitialMatches(data.request.id);
    } catch (err: any) {
      console.error("[Lead Capture Error]:", err);
      toast.error(err.message || "We couldn't submit your request right now. Please try again.");
    } finally {
      setIsSubmittingLead(false);
    }
  };

  const fetchInitialMatches = async (leadId: string) => {
    try {
      const { data, error } = await supabase
        .from('customer_request_matches')
        .select('*, businesses(*)')
        .eq('request_id', leadId);

      if (error) throw error;
      if (data) {
        setMatches(data);
      }
    } catch (err_matches) {
      console.error("Error retrieving initial matches:", err_matches);
    }
  };

  // Subscribe to Realtime Updates of business response matches
  useEffect(() => {
    if (step !== 'lead_capture' || subStep !== 'success_matching' || !createdLead?.id) {
      return;
    }

    // 1. Setup Supabase Realtime channel
    const channel = supabase
      .channel(`lead-matches-${createdLead.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'customer_request_matches',
          filter: `request_id=eq.${createdLead.id}`
        },
        async (payload: any) => {
          console.log('[Realtime Match Triggered]', payload);
          // Fetch complete payload including associated businesses table properties
          const { data, error } = await supabase
            .from('customer_request_matches')
            .select('*, businesses(*)')
            .eq('id', payload.new.id)
            .single();

          if (!error && data) {
            setMatches(prev => {
              const filterOut = prev.filter(m => m.id !== data.id);
              return [...filterOut, data];
            });
            toast.success(`A verified seller "${data.businesses?.business_name}" responded to your request!`);
          }
        }
      )
      .subscribe();

    // 2. Setup periodic poll for robust cellular compatibility
    const pollInterval = setInterval(() => {
      fetchInitialMatches(createdLead.id);
    }, 4500);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(pollInterval);
    };
  }, [step, subStep, createdLead?.id]);

  const resetAssistant = () => {
    setStep('lead_capture');
    setSelectedFaq(null);
    setSubStep('request_desc');
  };

  const faqs = [
    {
      q: "How does the AI Lead Broker match suppliers?",
      a: "When you state what you need, our matches run category filters, location boundaries, and verify merchants. Only verified businesses near your location receive direct push leads."
    },
    {
      q: "How do matched suppliers contact me?",
      a: "A customer who submits a phone number in a Sourcing Hub request is intentionally creating a contactable business lead. Relevant matched businesses receive your request so they can follow up directly even after you leave MyServiceHub."
    },
    {
      q: "Is it free to submit custom requests?",
      a: "Yes! Landing page customers can submit limitless request capturing without creating accounts. It incepts no financial cost."
    },
    {
      q: "How do I secure business strategy help?",
      a: "Select 'Start or grow a business' on the initial menu to summon 'Sia Assistant' for deep entrepreneurial ideation, business plans, and digital analytics guidance."
    }
  ];

  const renderWorkspace = () => {
    return (
      <div className={`flex flex-col h-full bg-[#090D1A] text-white overflow-hidden`}>
        {/* Header Panel */}
        <div className="bg-[#090D1A] text-white p-6 pb-8 shrink-0 relative overflow-hidden border-b border-white/5">
          <div className="absolute top-0 right-0 w-44 h-44 bg-blue-600/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          
          {!inline && (
            <button 
              onClick={() => setIsOpen(false)}
              className="absolute top-6 right-6 p-2 bg-white/5 hover:bg-white/10 rounded-full transition-colors text-slate-400 hover:text-white"
              title="Close Dialog"
            >
              <X className="w-5 h-5" />
            </button>
          )}

          <button 
            onClick={() => setStep(step === 'help' ? 'lead_capture' : 'help')}
            className={`absolute top-6 ${inline ? 'right-6' : 'right-16'} p-2 bg-white/5 hover:bg-white/10 rounded-full transition-colors text-slate-400 hover:text-white`}
            title="Privacy & Matching FAQs"
          >
            <HelpCircle className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3 mb-4">
            {step === 'lead_capture' && subStep !== 'request_desc' && subStep !== 'success_matching' && (
              <button 
                onClick={() => {
                  if (subStep === 'budget_input') setSubStep('request_desc');
                  else if (subStep === 'location_select') setSubStep('budget_input');
                  else if (subStep === 'contact_pref') setSubStep('location_select');
                  else if (subStep === 'phone_input') setSubStep('contact_pref');
                }}
                className="p-1 px-2.5 bg-white/5 hover:bg-white/10 rounded-xl transition-all flex items-center gap-1 text-[9px] font-bold text-slate-300 mr-2 uppercase tracking-wider"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </button>
            )}
            {step === 'help' && (
              <button 
                onClick={() => setStep('lead_capture')}
                className="p-1 px-2.5 bg-white/5 hover:bg-white/10 rounded-xl transition-all flex items-center gap-1 text-[9px] font-bold text-slate-300 mr-2 uppercase tracking-wider"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </button>
            )}
            <div className="bg-blue-600 p-2 rounded-xl text-yellow-300 shadow-lg shadow-blue-600/30">
              <Sparkles className="w-4 h-4" />
            </div>
            <span className="text-[9px] font-black uppercase tracking-[0.25em] text-blue-400">Sourcing Hub</span>
          </div>

          <h2 className="text-xl font-black tracking-tight leading-tight">
            {step === 'lead_capture' && "Source All Goods & Services"}
            {step === 'help' && "Vetting & Security FAQs"}
          </h2>
          <p className="text-slate-400 font-medium text-[11px] mt-1">
            {step === 'lead_capture' && "State what you need (spare parts, goods, or services) to query verified sellers instantly."}
            {step === 'help' && "Discover security protection protocols and matching metrics."}
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="h-1 bg-slate-850 w-full shrink-0">
          <motion.div 
            className="h-full bg-blue-600 shadow-md shadow-blue-500/50"
            animate={{ 
              width: step === 'help'
                ? '100%'
                : step === 'lead_capture'
                ? (subStep === 'request_desc'
                  ? '15%'
                  : subStep === 'budget_input'
                  ? '35%'
                  : subStep === 'location_select'
                  ? '55%'
                  : subStep === 'contact_pref'
                  ? '75%'
                  : subStep === 'phone_input'
                  ? '90%'
                  : '100%')
                : '100%'
            }}
            transition={{ duration: 0.3 }}
          />
        </div>

        {/* Body Workspace */}
        <div className="flex-1 overflow-y-auto bg-[#090D1A] no-scrollbar relative flex flex-col p-6 space-y-6">
          <AnimatePresence mode="wait">
            
            {/* Step: LEAD CAPTURE CHAMBER */}
            {step === 'lead_capture' && (
              <motion.div
                key="lead_capture"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-4 flex-1 flex flex-col"
              >
                {/* SUBSTEP 1: Request Specification */}
                {subStep === 'request_desc' && (
                  <div className="space-y-4 my-auto">
                    <label className="text-xs font-black uppercase text-slate-400 tracking-wider block">1. What are you looking for?</label>
                    <p className="text-[11px] text-slate-400 leading-relaxed">
                      Be as specific as possible (e.g., brand model, part size, or service terms) to fetch high-accuracy pricing from suppliers.
                    </p>
                    <textarea
                      value={leadRequest}
                      onChange={(e) => setLeadRequest(e.target.value)}
                      placeholder="Example: I need a Toyota Hilux 2018 gearbox, or a home deep cleaning service for 3 bedrooms..."
                      rows={4}
                      className="w-full p-4 rounded-[1.25rem] bg-slate-900 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs font-bold leading-relaxed resize-none"
                      autoFocus
                    />
                    <div className="pt-1">
                      <p className="text-[10px] font-black uppercase text-slate-500 mb-2">Popular demands:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {["Toyota D4D Alternator", "Borehole Drilling Service", "Residential Solar Package", "Home Laundry"].map((term) => (
                          <button
                            key={term}
                            onClick={() => setLeadRequest(term)}
                            className="text-[10px] font-extrabold px-3 py-1.5 bg-white/5 border border-white/5 text-slate-300 hover:bg-white/10 hover:text-white rounded-xl transition-all"
                          >
                            + {term}
                          </button>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={handleNextSubStep}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black uppercase tracking-wider text-xs py-3.5 rounded-xl shadow-lg mt-2 flex items-center justify-center gap-2"
                    >
                      Continue <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* SUBSTEP 2: Budget specification */}
                {subStep === 'budget_input' && (
                  <div className="space-y-4 my-auto">
                    <label className="text-xs font-black uppercase text-slate-400 tracking-wider block">2. What is your budget limit?</label>
                    <p className="text-[11px] text-slate-400 leading-relaxed">
                      Entering a budget ensures we match you with sellers who accommodate your pricing parameters. This is optional and can be skipped.
                    </p>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Minimum (BWP)</span>
                        <input 
                          type="number" 
                          placeholder="e.g. 500" 
                          value={budgetMin}
                          onChange={(e) => setBudgetMin(e.target.value)}
                          className="w-full p-3.5 rounded-xl bg-slate-905 border border-white/10 focus:ring-2 focus:ring-blue-500 text-xs font-black text-white focus:outline-none"
                        />
                      </div>
                      <div>
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Maximum (BWP)</span>
                        <input 
                          type="number" 
                          placeholder="e.g. 4000" 
                          value={budgetMax}
                          onChange={(e) => setBudgetMax(e.target.value)}
                          className="w-full p-3.5 rounded-xl bg-slate-905 border border-white/10 focus:ring-2 focus:ring-blue-500 text-xs font-black text-white focus:outline-none"
                        />
                      </div>
                    </div>

                    <div className="pt-2 flex gap-3">
                      <button
                        onClick={() => {
                          setBudgetMin('');
                          setBudgetMax('');
                          setSubStep('location_select');
                        }}
                        className="flex-1 bg-white/10 hover:bg-white/15 text-slate-300 text-xs font-black uppercase py-3.5 rounded-xl"
                      >
                        Skip Budget
                      </button>
                      <button
                        onClick={handleNextSubStep}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-black uppercase py-3.5 rounded-xl shadow-lg flex items-center justify-center gap-2"
                      >
                        Next <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* SUBSTEP 3: Location matching */}
                {subStep === 'location_select' && (
                  <div className="space-y-4 my-auto">
                    <label className="text-xs font-black uppercase text-slate-400 tracking-wider block">3. Which location/city are you in?</label>
                    <p className="text-[11px] text-slate-400 leading-relaxed">
                      Only suppliers who can deliver to or service your operational city will receive this lead.
                    </p>
                    
                    <div className="grid grid-cols-2 gap-1.5 max-h-[180px] overflow-y-auto pr-1 no-scrollbar">
                      {BOTSWANA_TOWNS.map((city) => (
                        <button
                          key={city}
                          onClick={() => setCustomerLocation(city)}
                          className={`p-3 rounded-xl text-[10px] font-black text-left transition-all border uppercase tracking-wider ${
                            customerLocation === city 
                              ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-600/10' 
                              : 'bg-white/5 border-white/5 text-slate-300 hover:bg-white/10'
                          }`}
                        >
                          <MapPin className="w-3 h-3 inline mr-1" />
                          {city}
                        </button>
                      ))}
                    </div>

                    <input 
                      type="text" 
                      placeholder="Or enter specific village/town..." 
                      value={customerLocation}
                      onChange={(e) => setCustomerLocation(e.target.value)}
                      className="w-full p-3.5 rounded-xl bg-slate-905 border border-white/10 focus:ring-2 focus:ring-blue-500 text-xs font-bold text-white focus:outline-none"
                    />

                    <button
                      onClick={handleNextSubStep}
                      disabled={!customerLocation}
                      className="w-full bg-blue-600 disabled:opacity-55 text-white font-black uppercase tracking-wider text-xs py-3.5 rounded-xl shadow-lg mt-2 flex items-center justify-center gap-2"
                    >
                      Add Location <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* SUBSTEP 4: Contact preference */}
                {subStep === 'contact_pref' && (
                  <div className="space-y-4 my-auto">
                    <label className="text-xs font-black uppercase text-slate-400 tracking-wider block">4. How should suppliers contact you?</label>
                    <p className="text-[11px] text-slate-400 leading-relaxed">
                      Your phone number is shared with relevant matched suppliers so they can review your request and reach out directly with custom quotes.
                    </p>

                    <div className="flex flex-col gap-2">
                      {[
                        { id: 'WhatsApp', label: 'WhatsApp', desc: 'Secure direct WhatsApp message to start chat' },
                        { id: 'Call', label: 'Call', desc: 'Standard cellular phone call' },
                        { id: 'Both', label: 'Both', desc: 'Allow both phone calls and WhatsApp messages' }
                      ].map((preference) => (
                        <button
                          key={preference.id}
                          onClick={() => setContactPreference(preference.id as any)}
                          className={`p-4 rounded-xl text-left transition-all border flex items-center justify-between ${
                            contactPreference === preference.id 
                              ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-600/10' 
                              : 'bg-white/5 border-white/5 text-slate-300 hover:bg-white/10'
                          }`}
                        >
                          <div>
                            <span className="font-extrabold text-xs uppercase block">{preference.label}</span>
                            <span className={`text-[9px] ${contactPreference === preference.id ? 'text-blue-100' : 'text-slate-500'}`}>{preference.desc}</span>
                          </div>
                          <Check className={`w-4 h-4 ${contactPreference === preference.id ? 'opacity-100' : 'opacity-0'}`} />
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={handleNextSubStep}
                      className="w-full bg-blue-600 text-white font-black uppercase tracking-wider text-xs py-3.5 rounded-xl shadow-lg mt-2 flex items-center justify-center gap-2"
                    >
                      Set Contact Mode <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* SUBSTEP 5: Contact phone submission */}
                {subStep === 'phone_input' && (
                  <div className="space-y-4 my-auto">
                    <label className="text-xs font-black uppercase text-slate-400 tracking-wider block">5. Provide Contact Phone Number</label>
                    <p className="text-[11px] text-slate-400 leading-relaxed font-medium">
                      Your phone number will be securely provided to authorized matched businesses so they can reach out to you via your chosen contact preference ({contactPreference || 'WhatsApp'}).
                    </p>

                    <div className="flex items-center gap-2 bg-slate-900 rounded-xl border border-white/10 p-1">
                      <span className="text-xs font-black text-slate-500 pl-3 select-none">+267</span>
                      <input 
                        type="tel" 
                        placeholder="77XXXXXX" 
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        className="flex-1 p-2.5 bg-transparent text-xs font-black text-white border-none outline-none focus:ring-0"
                        autoFocus
                      />
                    </div>

                    <button
                      onClick={handleNextSubStep}
                      disabled={isSubmittingLead || !phoneNumber.trim()}
                      className="w-full bg-blue-600 disabled:opacity-55 text-white font-black uppercase tracking-wider text-xs py-3.5 rounded-xl shadow-lg mt-2 flex items-center justify-center gap-2"
                    >
                      {isSubmittingLead ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin animate-infinite" /> Broadcasting...
                        </>
                      ) : (
                        <>
                          Find Suppliers <Check className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* SUBSTEP 6: Success match dashboard */}
                {subStep === 'success_matching' && createdLead && (
                  <div className="space-y-3 flex-1 flex flex-col min-h-0">
                    <div className="bg-blue-600 text-[#090D1A] p-4 rounded-2xl text-center shadow-md shrink-0">
                      <h4 className="font-extrabold text-xs uppercase tracking-wider mb-1">Status: Broadcasting matches...</h4>
                      <p className="text-[10px] leading-normal opacity-90 block text-slate-900 font-bold">
                        Suppliers in <strong>{createdLead.customer_location}</strong> matching category <strong>{createdLead.category?.replace('_', ' ').toUpperCase()}</strong> are currently reviewing your request.
                      </p>
                    </div>

                    <div className="flex-1 overflow-y-auto no-scrollbar min-h-0 space-y-2.5 pr-0.5">
                      <div className="flex items-center justify-between text-slate-400 font-black uppercase tracking-widest text-[9px]">
                        <span>Suppliers matched ({matches.length})</span>
                        {matches.length > 0 && <span className="text-emerald-400 flex items-center gap-1 animate-pulse"><CheckCircle2 className="w-3.5 h-3.5" /> quotes active</span>}
                      </div>

                      {matches.length === 0 ? (
                        <div className="py-10 text-center bg-white/5 rounded-2xl border border-white/5 flex flex-col items-center">
                          <Loader2 className="w-6 h-6 animate-spin text-blue-500 mb-2" />
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-relaxed">
                            Tender is live in supplier dashboards.<br />
                            responses standardly appear in 1-2 minutes.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2.5">
                          {matches.map((match) => {
                            const business = match.businesses;

                            return (
                              <div 
                                key={match.id} 
                                className="bg-white/5 border border-white/5 rounded-2xl p-4 space-y-3 transition-all"
                              >
                                <div className="flex items-start justify-between gap-4">
                                  <div>
                                    <span className="px-2 py-0.5 bg-blue-500/10 text-blue-400 border border-blue-500/20 text-[8px] font-extrabold tracking-widest uppercase rounded">
                                      {business?.verification_status?.replace('_', ' ')}
                                    </span>
                                    <h5 className="font-black text-white mt-1 leading-tight">{business?.business_name}</h5>
                                    <p className="text-[10px] text-slate-400 font-medium leading-relaxed mt-1">
                                      "{match.seller_response}"
                                    </p>
                                  </div>

                                  <div className="text-right shrink-0">
                                    <span className="text-[10px] font-bold text-slate-400 block uppercase tracking-wider">price quote</span>
                                    <span className="text-white font-black text-xs">
                                      {match.quoted_price ? `BWP ${match.quoted_price}` : "Flexible / Quote needed"}
                                    </span>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3 pt-2.5 border-t border-white/5 text-[9px]">
                                  <div>
                                    <span className="text-slate-500 font-extrabold uppercase block tracking-wider">delivery available</span>
                                    <span className="font-black text-slate-300">
                                      {match.delivery_available ? "✅ Yes" : "❌ Pickup Only"}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-slate-500 font-extrabold uppercase block tracking-wider">delivery duration</span>
                                    <span className="font-black text-slate-300">
                                      {match.estimated_delivery_time || "N/A"}
                                    </span>
                                  </div>
                                </div>

                                {/* Action buttons */}
                                <div className="pt-2">
                                  {business?.whatsapp_number && business.whatsapp_number.replace(/\D/g, '').length >= 8 ? (
                                    <a 
                                      href={`https://wa.me/${business.whatsapp_number.replace(/\D/g, '')}?text=Hi%20${encodeURIComponent(business?.business_name || 'Business')},%20I%20reviewed%20your%20quote%20of%20BWP%20${match.quoted_price}%20for%20${encodeURIComponent(createdLead.request_text)}%20on%20MyServiceHub.%20Let's%20discuss!`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="w-full bg-[#25D366] hover:bg-[#20ba59] text-white py-2 px-3 rounded-xl font-bold uppercase tracking-widest text-[9px] flex items-center justify-center gap-2 shadow-md transition-all"
                                    >
                                      <MessageCircle className="w-4 h-4" />
                                      Chat on WhatsApp
                                    </a>
                                  ) : (
                                    <div className="w-full bg-white/5 text-slate-400 py-2 px-3 rounded-xl font-bold uppercase tracking-widest text-[9px] flex items-center justify-center gap-2 border border-white/5 cursor-not-allowed">
                                      <MessageCircle className="w-4 h-4 text-slate-500" />
                                      WhatsApp unavailable
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    
                    <button 
                      onClick={() => {
                        if (window.confirm("Abandon broadcasting match feed? You can re-open it by submitting another description.")) {
                          resetAssistant();
                        }
                      }}
                      className="text-[9px] font-black uppercase text-slate-500 tracking-wider text-center block pt-1 hover:text-slate-300 cursor-pointer"
                    >
                      × Cancel Broadcasting Match
                    </button>
                  </div>
                )}
              </motion.div>
            )}

            {/* Step: FAQ AND PROTECTION INFO */}
            {step === 'help' && (
              <motion.div
                key="help"
                initial={{ opacity: 0, x: 15 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -15 }}
                className="space-y-2.5 flex-1"
              >
                {faqs.map((faq, idx) => (
                  <div key={idx} className="border border-white/5 bg-white/5 rounded-2xl overflow-hidden">
                    <button
                      onClick={() => setSelectedFaq(selectedFaq === idx ? null : idx)}
                      className="w-full p-3.5 flex items-center justify-between text-left hover:bg-white/10 transition-colors"
                    >
                      <span className="font-extrabold text-white text-xs">{faq.q}</span>
                      <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${selectedFaq === idx ? 'rotate-90' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {selectedFaq === idx && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="bg-slate-950 px-4 pb-4 overflow-hidden"
                        >
                          <p className="text-[11px] text-slate-400 leading-relaxed pt-2">
                            {faq.a}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
                
                <div className="space-y-4 pt-4 shrink-0">
                  <div className="p-4 rounded-2xl bg-emerald-950/10 border border-emerald-900/20 text-center">
                    <p className="text-[11px] font-bold text-emerald-300 mb-3 leading-relaxed">
                      Need platform assistance or direct escalations? Direct chat with administrators is active.
                    </p>
                    <a 
                      href="https://wa.me/26775902379"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 bg-[#25D366] hover:bg-[#20ba59] text-white px-4 py-2.5 rounded-xl font-bold uppercase tracking-widest text-[9px] shadow-lg"
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                      Support Hotline
                    </a>
                  </div>
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Floating Action Launcher - hidden on inline */}
      {!inline && (
        <motion.button
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.94 }}
          onClick={() => {
            setIsOpen(true);
            setStep('lead_capture');
            setSubStep('request_desc');
          }}
          className="fixed bottom-24 sm:bottom-28 right-6 z-[110] bg-blue-600 hover:bg-blue-700 text-white px-5 py-3.5 rounded-full shadow-2xl shadow-blue-600/40 flex items-center justify-center gap-2 font-black uppercase tracking-widest text-[10px] border border-blue-400/30 transition-all cursor-pointer"
          id="smart-assistant-trigger"
        >
          <Sparkles className="w-4 h-4 animate-pulse text-yellow-300" />
          Sourcing Hub
        </motion.button>
      )}

      {/* Backdrop & dialog wrapper for floating mode / render directly if inline */}
      {inline ? (
        <div className="w-full max-w-xl mx-auto rounded-[2.5rem] bg-[#090D1A] border border-white/10 flex flex-col h-[560px] overflow-hidden shadow-2xl relative z-10">
          {renderWorkspace()}
        </div>
      ) : (
        <AnimatePresence>
          {isOpen && (
            <div className="fixed inset-0 bg-slate-950/75 backdrop-blur-md z-[155] flex items-center justify-center p-4">
              {/* Assistant Panel Container */}
              <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 35, scale: 0.95 }}
                className="bg-white dark:bg-slate-900 w-full max-w-lg rounded-[2.5rem] shadow-2xl overflow-hidden border border-slate-100 dark:border-slate-800 flex flex-col h-[640px]"
              >
                {renderWorkspace()}
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      )}
    </>
  );
};

